import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ArticleCard from '../components/ArticleCard';
import CategoryBadge from '../components/CategoryBadge';
import ShareButtons from '../components/ShareButtons';
import BrunoMessage from '../components/BrunoMessage';

export default function BlogArticle() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [relatedArticles, setRelatedArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const fetchArticle = async () => {
      try {
        setLoading(true);
        const { data } = await axios.get(`/api/blog/artigos/${slug}`);
        setArticle(data.artigo);
        setRelatedArticles(data.relacionados || []);
        setNotFound(false);
      } catch (error) {
        if (error.response?.status === 404) {
          setNotFound(true);
        }
        console.error('Error fetching article:', error);
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      fetchArticle();
    }
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-muted/30">
        <Header />
        <div className="flex items-center justify-center h-96 pt-20">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            <p className="mt-4 text-muted-foreground">Carregando artigo...</p>
          </div>
        </div>
      </div>
    );
  }

  if (notFound || !article) {
    return (
      <div className="min-h-screen bg-muted/30">
        <Header />
        <div className="max-w-4xl mx-auto px-4 py-16 pt-20">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-secondary mb-4">Artigo Não Encontrado</h1>
            <p className="text-muted-foreground mb-8">Desculpe, o artigo que você está procurando não existe ou foi removido.</p>
            <Link to="/blog" className="inline-block bg-primary hover:opacity-90 text-white font-semibold py-3 px-6 rounded-lg transition">
              Voltar ao Blog
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const currentUrl = typeof window !== 'undefined' ? window.location.href : '';
  const readingTime = Math.ceil((article.conteudo?.split(' ').length || 0) / 200);

  return (
    <div className="min-h-screen bg-muted/30">
      <Header />

      {/* Breadcrumb */}
      <div className="bg-muted/40 border-b border-border pt-24">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <nav className="flex items-center space-x-2 text-sm text-muted-foreground">
            <Link to="/" className="hover:text-primary transition">Home</Link>
            <span>/</span>
            <Link to="/blog" className="hover:text-primary transition">Blog</Link>
            <span>/</span>
            {article.blog_categorias && (
              <>
                <span className="text-foreground">{article.blog_categorias.nome}</span>
                <span>/</span>
              </>
            )}
            <span className="text-foreground font-semibold">{article.titulo}</span>
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Article Content - 2 cols */}
          <div className="lg:col-span-2">
            {/* Cover Image */}
            {article.imagem_capa ? (
              <img
                src={article.imagem_capa}
                alt={article.titulo}
                className="w-full h-96 object-cover rounded-lg mb-8"
              />
            ) : (
              <div className="w-full h-96 bg-gradient-to-br from-orange-400 to-orange-600 rounded-lg mb-8"></div>
            )}

            {/* Category Badge */}
            {article.blog_categorias && (
              <div className="mb-4">
                <CategoryBadge categoria={article.blog_categorias} />
              </div>
            )}

            {/* Title */}
            <h1 className="text-4xl md:text-5xl font-bold text-secondary mb-6 leading-tight">
              {article.titulo}
            </h1>

            {/* Meta Info */}
            <div className="flex items-center gap-6 mb-8 pb-8 border-b border-border">
              <div className="flex items-center gap-3">
                <img
                  src="/images/brand/bruno-principal.png"
                  alt="Bruno"
                  className="w-12 h-12 rounded-full object-cover"
                />
                <div>
                  <p className="font-semibold text-secondary">Bruno</p>
                  <p className="text-sm text-muted-foreground">Autor</p>
                </div>
              </div>
              {article.published_at && (
                <div className="text-sm text-muted-foreground">
                  <p>{new Date(article.published_at).toLocaleDateString('pt-BR')}</p>
                </div>
              )}
              <div className="text-sm text-muted-foreground">
                <p>{readingTime} min de leitura</p>
              </div>
            </div>

            {/* Article Content */}
            <div className="article-content mb-12 prose prose-lg max-w-none">
              <style>{`
                .article-content h2 {
                  font-size: 2rem;
                  font-weight: 700;
                  color: #1a202c;
                  margin-top: 2rem;
                  margin-bottom: 1rem;
                  line-height: 1.2;
                }
                .article-content h3 {
                  font-size: 1.5rem;
                  font-weight: 600;
                  color: #2d3748;
                  margin-top: 1.5rem;
                  margin-bottom: 0.75rem;
                }
                .article-content p {
                  color: #4a5568;
                  line-height: 1.8;
                  margin-bottom: 1.5rem;
                  font-size: 1rem;
                }
                .article-content ul,
                .article-content ol {
                  color: #4a5568;
                  margin-bottom: 1.5rem;
                  padding-left: 2rem;
                  line-height: 1.8;
                }
                .article-content li {
                  margin-bottom: 0.5rem;
                }
                .article-content blockquote {
                  border-left: 4px solid #f97316;
                  padding-left: 1.5rem;
                  margin: 2rem 0;
                  color: #4a5568;
                  font-style: italic;
                }
                .article-content img {
                  max-width: 100%;
                  height: auto;
                  border-radius: 0.5rem;
                  margin: 2rem 0;
                }
                .article-content strong {
                  color: #1a202c;
                  font-weight: 600;
                }
              `}</style>
              <div dangerouslySetInnerHTML={{ __html: article.conteudo }} />
            </div>

            {/* Tags */}
            {article.tags && article.tags.length > 0 && (
              <div className="mb-12 pb-12 border-b border-border">
                <div className="flex flex-wrap gap-3">
                  {article.tags.map((tag, index) => (
                    <span key={index} className="inline-block bg-muted text-foreground text-sm px-3 py-1 rounded-full">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Share Buttons */}
            <div className="mb-12">
              <ShareButtons url={currentUrl} title={article.titulo} />
            </div>
          </div>

          {/* Sidebar - 1 col */}
          <div className="lg:col-span-1">
            {/* Author Card */}
            <div className="bg-card rounded-lg p-6 mb-8 shadow-card border border-border">
              <div className="text-center">
                <img
                  src="/images/brand/bruno-principal.png"
                  alt="Bruno"
                  className="w-24 h-24 rounded-full object-cover mx-auto mb-4"
                />
                <h3 className="text-xl font-semibold text-secondary mb-2">Bruno</h3>
                <p className="text-sm text-muted-foreground">
                  Engenheiro Civil especialista em orçamentos de obra
                </p>
              </div>
            </div>

            {/* Related Articles */}
            {relatedArticles.length > 0 && (
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-secondary mb-4">Artigos Relacionados</h3>
                <div className="space-y-4">
                  {relatedArticles.slice(0, 3).map((relatedArticle) => (
                    <ArticleCard
                      key={relatedArticle.id}
                      artigo={relatedArticle}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* CTA Card */}
            <div className="bg-gradient-to-br from-orange-400 to-orange-600 rounded-lg p-6 text-white">
              <h3 className="text-lg font-semibold mb-3">Quanto vai custar sua obra?</h3>
              <p className="text-sm text-orange-100 mb-4">
                Obtenha um orçamento personalizado com nossos especialistas.
              </p>
              <Link
                to="/orcamento"
                className="inline-block w-full bg-white text-orange-600 font-semibold py-3 px-4 rounded-lg text-center hover:bg-orange-50 transition"
              >
                Solicitar Orçamento
              </Link>
            </div>

            {/* Bruno Message */}
            <div className="mt-8">
              <BrunoMessage pose="handshake" message="Gostou do artigo? Calcule o orçamento da sua obra com nossa ferramenta!" />
            </div>
          </div>
        </div>
      </div>

      {/* Related Articles Section */}
      {relatedArticles.length > 0 && (
        <div className="bg-card border-t border-border py-12">
          <div className="max-w-7xl mx-auto px-4">
            <h2 className="text-3xl font-bold text-secondary mb-8">Artigos Relacionados</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {relatedArticles.slice(0, 3).map((relatedArticle) => (
                <ArticleCard key={relatedArticle.id} artigo={relatedArticle} />
              ))}
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
